import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaCogs, FaProjectDiagram, FaFileInvoiceDollar, FaUserClock, FaRobot, FaSyncAlt,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaRegClipboard, FaRegClock,
    FaTasks, FaNetworkWired,
    // Added missing imports to fix ReferenceError
    FaCode, FaSearch, FaPencilRuler, FaCheckDouble, FaRocket
} from 'react-icons/fa';
import heroImage from '../../assets/ad.png';

const ProcessAutomationPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaRobot, title: 'Robotic Process Automation', desc: 'Siftware bots that mimic human actions to execute repetitive tasks 24/7.', image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600' },
        { icon: FaFileInvoiceDollar, title: 'Invoice Processing', desc: 'Automate data extraction, validation, and entry from invoices into your ERP.', image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600' },
        { icon: FaSyncAlt, title: 'Workflow Integration', desc: 'Connect disparate systems (CRM, ERP, Email) to enable seamless data flow.', image: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=600' },
        { icon: FaUserClock, title: 'HR Onboarding', desc: 'Streamline employee onboarding with automated document collection and provisioning.', image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600' },
        { icon: FaRegClipboard, title: 'Compliance Reporting', desc: 'Generate complex regulatory reports automatically and error-free.', image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600' },
        { icon: FaProjectDiagram, title: 'Supply Chain Ops', desc: 'Automate inventory tracking, order processing, and shipment notifications.', image: 'https://images.unsplash.com/photo-1566576912902-199bd620fae5?w=600' }
    ];

    const techStack = [
        { name: 'UiPath', icon: FaRobot },
        { name: 'Automation Anywhere', icon: FaCogs },
        { name: 'Microsoft Power Automate', icon: FaNetworkWired },
        { name: 'Zapier', icon: FaSyncAlt },
        { name: 'Python Scripts', icon: FaCode },
        { name: 'Blue Prism', icon: FaProjectDiagram }
    ];

    // Horizontal Flow Steps (Automation Flow)
    const flowSteps = [
        { title: 'Map', icon: FaProjectDiagram, color: 'from-blue-500 to-indigo-500' },
        { title: 'Analyze', icon: FaSearch, color: 'from-cyan-500 to-teal-500' },
        { title: 'Design', icon: FaPencilRuler, color: 'from-orange-400 to-red-500' },
        { title: 'Develop', icon: FaCogs, color: 'from-purple-500 to-pink-500' },
        { title: 'Test', icon: FaCheckDouble, color: 'from-green-500 to-emerald-500' },
        { title: 'Scale', icon: FaRocket, color: 'from-indigo-600 to-blue-700' }
    ];

    const faqs = [
        { q: 'What is RPA?', a: 'Robotic Process Automation (RPA) uses software "bots" to perform rule-based tasks just like a human would, but faster and without errors.' },
        { q: 'Will automation replace my employees?', a: 'No, it liberates them. By automating boring, repetitive data entry, your team can focus on high-value strategy and customer service.' },
        { q: 'How fast can we deploy a bot?', a: 'Simple workflows can be automated in 2-4 weeks. Complex enterprise integrations usually take 8-12 weeks.' },
        { q: 'What systems can you integrate?', a: 'Virtually anything. If a human can interact with it via a screen or API, our bots can automate it. This includes legacy mainframes and modern web apps.' },
        { q: 'What is the ROI?', a: 'Most clients see a positive ROI within 6-9 months due to labor savings, error reduction, and increased throughput.' }
    ];

    const whyChooseUs = [
        { title: 'Process-First Approach', desc: 'We don\'t automate bad processes. We optimize them first, then automate.' },
        { title: 'Bank-Grade Security', desc: 'Our bots operate with encrypted credentials and audit trails compliant with SOC2/GDPR.' },
        { title: 'Exception Handling', desc: 'We build robust bots that know what to do when things go wrong, ensuring stability.' },
        { title: 'Vendor Neutral', desc: 'We select the right platform (UiPath, Power Automate, Custom) for your specific budget and needs.' },
        { title: 'Scalable Workforce', desc: 'Spin up 1 bot or 100 bots instantly to handle peak workloads.' },
        { title: 'Intelligent Automation', desc: 'We combine RPA with AI (OCR, NLP) to handle unstructured data like emails and PDFs.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-indigo-900/90 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-blue-300 mb-4">Efficiency &gt; Scale</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Process <br /><span className="text-blue-400 font-normal">Automation</span></h1>
                        <p className="text-lg font-light text-blue-100 leading-relaxed mb-8 max-w-2xl">
                            Stop wasting human potential on robotic tasks. Streamline operations with intelligent workflows.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href="#inquiry" className="px-8 py-3 bg-blue-600 text-white font-medium rounded-sm hover:bg-blue-700 hover:shadow-lg transition-all text-sm uppercase tracking-wide">
                                Automate Now
                            </a>
                            <a href="#services" className="px-8 py-3 border border-blue-300 text-white font-medium rounded-sm hover:bg-white hover:text-blue-900 transition-all text-sm uppercase tracking-wide">
                                Use Cases
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6">The Cost of Manual Work</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Errors, delays, and employee burnout are costing you millions.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-12 text-center">
                        {[
                            { label: 'Hours Saved / Yr', val: '50,000+' },
                            { label: 'Error Reduction', val: '100%' },
                            { label: 'Processing Speed', val: '10x' },
                            { label: '24/7 Availability', val: 'Yes' }
                        ].map((stat, idx) => (
                            <div key={idx} className="relative p-6 group">
                                <div className="absolute inset-0 bg-white rounded-sm shadow-sm transform transition-transform group-hover:-translate-y-2 group-hover:shadow-md"></div>
                                <div className="relative z-10">
                                    <div className="text-5xl font-bold text-blue-600 mb-3">{stat.val}</div>
                                    <div className="text-sm text-slate-500 uppercase tracking-widest">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Automation Areas</h2>
                        <p className="text-lg text-slate-600">
                            Where we inject speed and accuracy.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-80 overflow-hidden rounded-sm bg-slate-900 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center opacity-60 transition-transform duration-700 group-hover:scale-110 group-hover:opacity-40" style={{ backgroundImage: `url(${service.image})` }}></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-900 via-transparent to-transparent opacity-90"></div>

                                <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                                    <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                                        <service.icon className="text-4xl text-blue-400 mb-4" />
                                        <h3 className="text-2xl font-light text-white mb-2">{service.title}</h3>
                                        <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-500">
                                            <p className="text-blue-100 text-sm leading-relaxed mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{service.desc}</p>
                                        </div>
                                    </div>
                                    <div className="w-12 h-1 bg-blue-500 mt-4 transform origin-left transition-all duration-500 group-hover:w-full"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="py-20 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <p className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10">Compatible Platforms</p>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
                        {techStack.map((tech, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-2 group cursor-default">
                                <tech.icon className="text-4xl text-slate-600 group-hover:text-blue-600 transition-colors" />
                                <span className="text-sm font-medium text-slate-500 group-hover:text-slate-800">{tech.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Implementation Process) */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Automation Roadmap</h2>
                        <p className="text-slate-600">Our seamless integration methodology.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-0.5 bg-slate-200 -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-12 gap-x-4 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">
                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20 mb-6"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-2xl shadow-xl group-hover:scale-110 transition-transform duration-300 ring-4 ring-white`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 text-center">{step.title}</h3>

                                    {/* Mobile Connector */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute -bottom-8 left-1/2 w-0.5 h-8 bg-slate-200"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-blue-900 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-blue-400 font-bold tracking-widest uppercase mb-4">Why Tech-Inovant Automation</p>
                            <h2 className="text-4xl md:text-5xl font-light mb-8 leading-tight">Reliability <br />Guaranteed</h2>
                            <p className="text-blue-200 text-lg leading-relaxed mb-8">
                                We build bots that don't break. Our "resilient design" philosophy ensures your automated processes keep running, even when underlying systems update.
                            </p>
                            <a href="#inquiry" className="inline-flex items-center text-blue-400 font-medium hover:text-white transition-colors">
                                Calculate Your Savings <FaChevronRight className="ml-2 text-xs" />
                            </a>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {whyChooseUs.map((item, idx) => (
                                <div key={idx} className="bg-blue-800/50 p-6 rounded-sm border border-blue-700 hover:border-cyan-500/50 transition-colors">
                                    <FaCheckCircle className="text-cyan-500 text-xl mb-3" />
                                    <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                                    <p className="text-blue-200 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Automation FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="border-b border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full py-6 text-left flex justify-between items-center group"
                                        >
                                            <span className="text-lg text-slate-800 font-light group-hover:text-blue-600 transition-colors">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-blue-500" /> : <FaChevronDown className="text-slate-300" />}
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{ height: openFaq === idx ? 'auto' : 0, opacity: openFaq === idx ? 1 : 0 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 text-slate-500 leading-relaxed">{faq.a}</p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-10 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Eliminate Manual Work</h2>
                            <p className="text-slate-500 mb-8">Which process is slowing you down?</p>

                            <form className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="Jane Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="Acme Inc." />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="jane@acme.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Pain Point</label>
                                    <select className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none transition-colors text-slate-700">
                                        <option>Invoice / Data Entry</option>
                                        <option>User Onboarding</option>
                                        <option>Reporting / Analytics</option>
                                        <option>System Integration</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-blue-600 text-white font-bold py-4 rounded-sm hover:bg-blue-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Get Automation Audit
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProcessAutomationPage;
